import { useEffect, useRef, useState } from "react";
import { ChangeStage, ChangeStageComment } from "../../types/pipeline";
import { useGetStagesQuery } from "../../api/pipelineApi";
import { useChangeDealStageMutation } from "../../api/pipelineApi";
import { ModalContainer } from "../UtilitiesComponents/ModalContainer";
import { getErrorMessage } from "../../utils/errorHelpers";
import { AddCommnetChangeStage } from "../PipelineComponents/EditComponents/AddCommentChangeStage";
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

interface Props {
    empresaId: number;
    pipelineId: number;
    dealId?: number;
    currentStageId: number;
    currentFechaVisita: string;
    nombreProspectador: string;
    estadoEmpresa: string;
}

export function ChangeDealStage({ empresaId, pipelineId, dealId, currentStageId, currentFechaVisita, nombreProspectador, estadoEmpresa }: Props) {
    const modalRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [changeDealStage] = useChangeDealStageMutation();
    const [showMenu, setShowMenu] = useState(false);
    const [currentStage, setCurrentStage] = useState(currentStageId);
    const [showModal, setShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        data,
        isSuccess,
        isError,
    } = useGetStagesQuery(pipelineId);

    const stages = dealId == undefined
        ? data?.filter(stage => stage.nombre != "Campañas realizadas") 
        : data;

    const stageInfo = isSuccess && stages ? stages?.find((stage) => stage.id == currentStage)?.nombre : "default";
    const [currentStageName, setCurrentStageName] = useState(stageInfo);
    const [newStageName, setNewStageName] = useState<string | null>(null);
    const [newStage, setNewStage] = useState<ChangeStage>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const updateStage = async (comment: ChangeStageComment) => {
        try {
            if(!newStage)
                return;

            setIsLoading(true);

            if(comment.visitaInfo != null)
            {
                comment.visitaInfo.horaFin = `${comment.visitaInfo.horaFin}:00`;
                comment.visitaInfo.horaInicio = `${comment.visitaInfo.horaInicio}:00`;
            }

            if (newStage.newStageId != 5)
                comment.visitaInfo = undefined;

            const request = {...newStage, ...comment};

            await changeDealStage(request).unwrap();

            setCurrentStage(newStage.newStageId);
            setCurrentStageName(newStage.stageName);

            setIsLoading(false);
            setShowMenu(false);
            setShowModal(false);
            
        } catch (error) {
            setIsLoading(false);
            setErrorMessage(getErrorMessage(error));
        }
    }

    const onSelectStage = async (event: React.MouseEvent<HTMLDivElement>) => {
        const stageSelected = event.currentTarget.textContent;

        if(dealId == undefined && stageSelected == "Campañas realizadas") return;

        if(stageSelected) {

            const newStageInfo = stages?.find((stage) => stage.nombre == stageSelected);

            if(newStageInfo?.id == currentStage)
                return;

            const newStage: ChangeStage = {
                empresaId: empresaId,
                newStageId: newStageInfo?.id ?? 0,
                pipelineId: pipelineId, 
                dealId: dealId,
                stageName: newStageInfo?.nombre ?? "Sin nombre"
            }

            setNewStageName(newStageInfo?.nombre ?? null);
            setNewStage(newStage);

            setShowModal(true);
            setShowMenu(false);
        }
    }

    const handleClickOutside = (event: MouseEvent) => {
        if(modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target as Node)) {
            setShowMenu(false);
        }
    }

    useEffect(() => {
        setCurrentStageName(stageInfo);
    }, [stageInfo]);

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        }
    }, []);

    if(!isSuccess || isError) {
        console.error("Ha ocurrido un error al cargar la informacion");
        return;
    }

    return (
        <div className="relative text-left col-span-2 flex flex-col sm:flex-row">
            <button 
                ref={buttonRef}
                onClick={() => setShowMenu(!showMenu)}
                id="dropdownButton" 
                type="button" 
                className={`
                    px-[10px] py-1 inline-flex justify-center bg-white text-sm font-medium  
                    hover:bg-gray-50 focus:outline-none items-center w-[285px]`} 
                aria-haspopup="true" 
                aria-expanded="true">

                <p className="font-semibold text-sm">{currentStageName}</p>

                <div className={`block transform transition-transform duration-300 ${showMenu ? 'rotate-180' : '-rotate-90 sm:rotate-0'}`}>
                    <ArrowDropDownRoundedIcon />
                </div>

            </button>

            <div 
                id="dropdownMenu" 
                ref={modalRef}
                className={`${!showMenu ? "hidden" : ""} z-50 origin-top-right absolute -right-0 top-[27px] mt-2 shadow-lg bg-white ring-1 ring-black ring-opacity-5 w-[285px] rounded border border-gray-300`}>
                <div className="py-0 overflow-auto max-h-[285px] ">
                    {isSuccess && stages?.map((stage, index) => (
                    <div 
                        key={index}
                        onClick={onSelectStage}
                        className="px-[10px] py-2 hover:bg-gray-100 flex items-center gap-x-1">
                        {currentStage === stage.id
                            ? <CheckCircleRoundedIcon sx={{ height: 15, width: 15 }}/>
                            : <div className="w-[15px] h-[15px]"></div>
                        }
                        <p className="font-semibold text-sm cursor-pointer">{stage.nombre}</p>
                    </div>
                    ))}
                </div>
            </div>

            {showModal && (
                <ModalContainer>
                    <AddCommnetChangeStage 
                        empresaId={empresaId}
                        dealId={dealId ?? 0}
                        nombreProspectador={nombreProspectador}
                        currentFechaVisita={currentFechaVisita}
                        updateStage={updateStage}
                        setShowModal={setShowModal}
                        setErrorMessage={setErrorMessage}
                        currentStageName={currentStageName}
                        newStageName={newStageName}
                        errorMessage={errorMessage}
                        isLoading={isLoading}
                        estadoEmpresa={estadoEmpresa}/>
                </ModalContainer>
            )}
        </div>
    );
}